import { ref } from 'vue';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  type RemoteConfig,
  getRemoteConfig,
  getAll,
  fetchAndActivate,
  getValue,
} from 'firebase/remote-config';
import { defineStore } from 'pinia';
import { isNil } from 'lodash-es';

type CampaignPromocode =
  | {
      code: string;
      isDomestic: boolean;
      airlines: string[] | null;
    }
  | '';

export type SerializedRemoteConfig = Record<
  string,
  boolean | string | CampaignPromocode | object
>;

const defaultConfig = {
  app_version: '',
  rwfree_zhuma_promo: '',
  rwfree_jasyl_juma_promo: '',
  trfree_show_cashback_hold: false,
  trfree_show_combo_pay_badge: true,
  trfree_show_cabinet_support_block: false,
  trfree_countdown_banner_config: {},
  avfree_campaign_promocode: '',
  avfree_features: '',
  avfree_war_zone_banner: {},
  avfree_quiz_config: {},
  cabfree_call_center_unavailable: {},
  cabfree_meta_migration: false,
  cabfree_show_esim_cross_sell: false,
  cabfree_show_insurance_cross_sell: false,
  cabfree_show_ticketon_main_button: false,
  cabfree_show_send_email: false,
  avfree_middle_east_war_zone_modal: {},
  avfree_details_warning_banner: {},
  avfree_details_sale_banner: false,
  free_digital_nauryz_banner_enabled: false,
  avfree_promocode_modal: {},
  avfree_serp_loader_asset: {},
  avfree_pax_data_auto_filling_by_photo_enabled: false,
} as const;

const parseJson = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return { isValid: true, value: parsed };
  } catch {
    return { isValid: false, value };
  }
};

const serializeRemoteConfig = (
  remoteConfig: RemoteConfig,
): SerializedRemoteConfig => {
  const defaultConfigKeys = Object.keys(defaultConfig);
  const remoteParameters = getAll(remoteConfig);

  return Object.keys(remoteParameters).reduce((acc, key) => {
    if (defaultConfigKeys.includes(key)) {
      const rawValue = remoteParameters[key].asString();
      const { isValid, value } = parseJson(rawValue);

      acc[key] = isValid ? value : rawValue;
    }
    return acc;
  }, {} as SerializedRemoteConfig);
};

export const useFirebaseStore = defineStore('firebase', () => {
  const serializedRemoteConfig = ref<SerializedRemoteConfig | null>(null);
  const isLoading = ref(false);

  let app: FirebaseApp;
  let rc: RemoteConfig;

  const init = (firebaseConfig: Record<string, string>) => {
    app = initializeApp(firebaseConfig);

    rc = getRemoteConfig(app);
    rc.defaultConfig = defaultConfig;
    rc.settings.minimumFetchIntervalMillis = 120000;

    isLoading.value = true;
    return fetchAndActivate(rc)
      .then(() => {
        serializedRemoteConfig.value = serializeRemoteConfig(rc);
        isLoading.value = false;
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const getBooleanValue = (key: keyof typeof defaultConfig) => {
    if (isNil(rc)) {
      return { value: Boolean(defaultConfig[key]), source: 'default' as const };
    }

    const remoteValue = getValue(rc, key);
    return { value: remoteValue.asBoolean(), source: remoteValue.getSource() };
  };

  return {
    serializedRemoteConfig,
    init,
    getBooleanValue,
    isLoading,
  };
});
